import { inject, Injectable } from "@angular/core";
import { PcpTokenDecoderService } from "./pcp-token-decoder.service";
import { catchError, filter, first, map, pairwise, switchMap } from "rxjs/operators";
import { ComponentStore } from "@ngrx/component-store";
import { finalize, Observable, of } from "rxjs";
import { Store } from "@ngrx/store";
import { authServiceActions, liveLessonAuthActions } from "../store/auth-service.actions";
import { TokensAndDecodedToken } from "../interfaces/token-error";
import { AuthApiService } from "./auth-api.service";
import { PersistentTokenStorageService } from "./persistent-token-storage.service";
import { LiveLessonId } from "@pcp/core/interfaces";
import { PcpLiveTokenDecoder } from "../live-auth-services/pcp-live-token-decoder.service";
import { AzureCommunicationsTokenUserResponse } from "../interfaces/live-lesson-token-response";
import { isPcpLiveSessionToken, PcpLiveSessionToken } from "../interfaces/live-lesson-token";
import { RestoreTokenHelperService } from "./restore-token-helper-service";
import { AuthBroadcastChannelService } from "./auth-broadcast-channel.service";
import { BROADCAST_USER_LOGOUT } from "../injection-tokens";
import { PcpLiveLoginTokenAuthenticator } from "../live-auth-services/client/pcp-live-login-token-authenticator.service";
import { HttpErrorResponse } from "@angular/common/http";
import { TokenRefreshOrchestratorService, TokenRefreshDelegate } from "./token-refresh-orchestrator.service";
import { AuthConfigService } from "./auth-config.service";


interface PcpAuthServiceState {
    /** There's a delay at application startup until a stored token is validated */
    isInitialized: boolean;

    tokenData?: TokensAndDecodedToken;
    isLoggingIn: boolean;
    isRefreshingToken: boolean;
    loginFailedReason?: string;
    isAuthenticated?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class PcpAuthService extends ComponentStore<PcpAuthServiceState> implements TokenRefreshDelegate {

    private readonly store = inject(Store);
    private readonly authBroadcastChannel = inject(AuthBroadcastChannelService);

    /** Fetch JWT for non-live lessons using credentials */
    private readonly authApiService = inject(AuthApiService);

    /** Fetch JWT for LiveLessons using tokens */
    private readonly liveLessonLoginTokenAuthenticator = inject(PcpLiveLoginTokenAuthenticator);

    /** Stores tokens in the localStorage or sessionStorage */
    private readonly tokenStorage = inject(PersistentTokenStorageService);

    /** Token decoder for non-live tokens */
    private readonly pcpTokenDecoder = inject(PcpTokenDecoderService);

    /** Token decoder for live tokens */
    private readonly pcpLiveTokenDecoder = inject(PcpLiveTokenDecoder);

    /** boolean: broadcast message when user gets logged out */
    private readonly broadcastLogout = inject(BROADCAST_USER_LOGOUT);

    /** Orchestrates complex token refresh flow */
    private readonly tokenRefreshOrchestrator = inject(TokenRefreshOrchestratorService);

    /** Provides authentication configuration */
    private readonly authConfig = inject(AuthConfigService);

    readonly isInitialized$ = this.select(state => state.isInitialized);
    private readonly stateWhenInitialized$ = this.isInitialized$.pipe(
        first(isInitialized => isInitialized),
        switchMap(() => this.state$),
    );

    private readonly tokenData$ = this.select(
        // this.stateWhenInitialized$,
        state => state.tokenData
    );

    /**
     * @deprecated
     * User is authenticated.
     * If the authenticated state is not yet determinded,
     * the value will be immediately resolved to false.
     */
    readonly isAuthenticated$ = this.select(s => s.isAuthenticated === true);

    /**
     * true: user is authenticated
     * false: user is not authenticated
     * undefined: not yet determined (e.g. during startup before token is restored)
     */
    readonly isAuthenticatedOrUndefined$ = this.select(s => s.isAuthenticated);

    // TODO: add selector with delay until initialized$ ?
    //       would mitigate the need to store `isInitialized` in the auth state

    readonly claims$ = this.select(this.tokenData$, tokenData => tokenData?.claims);
    readonly customer$ = this.select(this.claims$, claims => claims?.customer);
    readonly userId$ = this.select(this.claims$, claims => claims?.userId);
    readonly sas$ = this.select(this.claims$, claims => claims?.sas);

    readonly accessToken$ = this.select(this.tokenData$, tokenData => tokenData?.token);
    readonly refreshToken$ = this.select(this.tokenData$, tokenData => tokenData?.refreshToken);

    readonly isLoggingIn$ = this.select(state => state.isLoggingIn);
    readonly isRefreshingToken$ = this.select(state => state.isRefreshingToken);
    readonly loginFailedReason$ = this.select(state => state.loginFailedReason);

    readonly loggedOut$ = this.isAuthenticated$.pipe(
        pairwise(),
        filter(([prev, next]) => prev !== next),
        map(([prev, next]) => next === false),
    );

    canRefreshToken() {
        const tokenData = this.get().tokenData;
        return tokenData?.token && tokenData?.refreshToken;
    }

    constructor() {
        super({
            tokenData: undefined,
            isLoggingIn: false,
            isRefreshingToken: false,
            isInitialized: false,
        });

        // Set this service as the auth service reference for the orchestrator
        this.tokenRefreshOrchestrator.setAuthService(this);
    }

    loginAsTeacherUsingUsernameAndPassword(username: string, password: string) {
        this.patchState({ isLoggingIn: true });
        return this.authApiService.authWithCredentials(username, password).pipe(this.handleLoginResponse());
    }

    loginAsTeacherUsingDongle(serials: string[]) {
        this.patchState({ isLoggingIn: true });
        return this.authApiService.authWithSerials(serials).pipe(this.handleLoginResponse());
    }

    loginAsBeamer(beamerAuthCode: string) {
        this.patchState({ isLoggingIn: true });
        return this.authApiService.authWithLessonCode(beamerAuthCode).pipe(this.handleLoginResponse());
    }

    loginAsLiveLessonModerator(liveLessonId: LiveLessonId, token: string) {
        this.patchState({ isLoggingIn: true });
        return this.liveLessonLoginTokenAuthenticator.getTokenForModerator(liveLessonId, token).pipe(
            this.handleLiveLessonLoginResponse(),
        );
    }

    loginAsLiveLessonStudent(liveLessonId: LiveLessonId, token: string): Observable<TokensAndDecodedToken> {
        this.patchState({ isLoggingIn: true });
        return this.liveLessonLoginTokenAuthenticator.getTokenForStudent(liveLessonId, token).pipe(
            this.handleLiveLessonLoginResponse(),
        );
    }

    /**
     * Login student for non-live lessons, e.g. push-the-button-student
     */
    loginAsStudentWithLessonCodeAndEnetToken(lessonCode: string, enetToken: string) {
        this.patchState({ isLoggingIn: true });
        return this.authApiService.authAsStudentWithRegistrationCodeAndEnetToken(lessonCode, enetToken).pipe(
            this.handleLoginResponse(),
        );
    }

    refreshToken() {
        this.patchState({ isRefreshingToken: true });
        const tokenData = this.get().tokenData;
        if (!tokenData) {
            throw new Error('No token to refresh');
        }

        const { token, refreshToken } = tokenData;
        return this.tokenRefreshOrchestrator.orchestrateTokenRefresh(token, refreshToken, this);
    }

    /**
     * Return true if the token needs to be refreshed.
     *
     * A token needs refresh if:
     * - is already expired
     * - or will expire soon
     */
    isTokenRefreshRequired() {
        const tokenData = this.get().tokenData;
        if (!tokenData) {
            throw new Error('No token to refresh');
        }

        return this.authConfig.isTokenRefreshRequired(tokenData.expiresAt);
    }

    /**
     * Same as `refreshToken`, but only if needed.
     *
     * noop if token is still valid
     */
    refreshTokenIfRequired() {
        const tokenData = this.get().tokenData;
        if (!tokenData) {
            throw new Error('No token to refresh');
        }

        return this.isTokenRefreshRequired() ? this.refreshToken() : of(tokenData);
    }

    getAuthToken() {
        return this.get().tokenData?.token;
    }

    markAsInitialized() {
        this.patchState({ isInitialized: true });
    }

    private readonly restoreTokenHelper = inject(RestoreTokenHelperService);

    /**
     * Restore token for non-live lessons.
     */
    async restoreToken() {
        return this.restoreTokenHelper.restoreToken({
            onRequested: (token: string, refreshToken: string) => {
                this.store.dispatch(authServiceActions.tokenRestoreRequested({ token, refreshToken }));
            },
            onSuccess: (tokenData: TokensAndDecodedToken) => {
                this.saveNewTokenData(tokenData);
                this.store.dispatch(authServiceActions.tokenRestoreSuccess({ tokenData }));
                this.patchState({ isAuthenticated: true });
            },
            onFailed: (error: any) => {
                this.clearToken();
                this.store.dispatch(authServiceActions.tokenRestoreFailed({ error }));
                this.patchState({ isAuthenticated: false });
            },
            performTokenRefresh: (token: string, refreshToken: string) => this.performTokenRefresh(token,  refreshToken),
        });
    }

    /**
     * Restore token for live lessons.
     */
    async restoreLiveLessonToken() {

        // cancel restore if the user wants to login (automatically)
        // NOTE: can't use the Angular Router as it might not be initialized yet at this point.
        // TODO: would be better to make this configurable
        // TODO: even better if we would separate the restore functionality into a separate "integration" library
        const url = window.location.href;
        if (url.includes('login')) {
            this.markAsInitialized();
            return;
        }

        return this.restoreTokenHelper.restoreLiveLessonToken({
            onRequested: (token: string, refreshToken: string, liveLessonToken: string) => {
                this.store.dispatch(liveLessonAuthActions.tokenRestoreRequested({ token, refreshToken, liveLessonToken }));
            },
            onSuccess: (tokenData: TokensAndDecodedToken, liveLessonToken: PcpLiveSessionToken) => {
                this.saveNewTokenData(tokenData);
                this.store.dispatch(liveLessonAuthActions.tokenRestoreSuccess({
                    tokenData: {
                        ...tokenData,
                        liveLesson: liveLessonToken,
                    },
                }));
            },
            onFailed: (error: any) => {
                this.clearToken();
                this.store.dispatch(liveLessonAuthActions.tokenRestoreFailed({ error }));
            },
            performTokenRefresh: (token: string, refreshToken: string) => this.performTokenRefresh(token,  refreshToken),
        });
    }

    logOut(dispatch = true) {
        this.tokenStorage.clear();
        this.patchState({ tokenData: undefined, isAuthenticated: false });
        this.store.dispatch(authServiceActions.userHasBeenLoggedOut());

        // NOTE: `broadcastLogout` is a global config and `dispatch` is a local option
        //       both boolean values need to be true.
        if (this.broadcastLogout && dispatch) {
            this.authBroadcastChannel.postLogout();
        }
    }

    private handleLoginResponse<T extends TokensAndDecodedToken>() {
        this.patchState({ loginFailedReason: undefined });

        return (response: Observable<T>) => response.pipe(
            map((tokenData: TokensAndDecodedToken) => {
                this.saveNewTokenData(tokenData);
                this.store.dispatch(authServiceActions.userLoginSuccess({ tokenData }));
                this.patchState({ isAuthenticated: true });

                return tokenData;
            }),
            catchError(err => {
                const error = err instanceof HttpErrorResponse ? err.error : err;

                this.clearToken();
                this.store.dispatch(authServiceActions.userLoginFailed({ error }));
                this.patchState({ loginFailedReason: error.message, isAuthenticated: false });

                throw error;
            }),
        );
    }

    private handleLiveLessonLoginResponse<T extends AzureCommunicationsTokenUserResponse>() {
        this.patchState({ loginFailedReason: undefined });

        return (response: Observable<T>) => response.pipe(
            map((liveLessonTokenData) => {
                const pcpLiveAuthToken = liveLessonTokenData.token;
                const decodedToken = this.pcpLiveTokenDecoder.decodeToken(pcpLiveAuthToken);
                if (!isPcpLiveSessionToken(decodedToken)) {
                    throw new Error('Invalid PCP Live token');
                }

                const pcpApiToken = decodedToken.pcpApiToken;

                const decodedPcpApiToken = this.pcpTokenDecoder.decodeAndValidateToken({
                    token: pcpApiToken,
                    refreshToken: '_TODO_refresh_token_is_not_set_yet_',
                });


                const tokenData: TokensAndDecodedToken = {
                    token: pcpApiToken,
                    refreshToken: '_TODO_refresh_token_is_not_set_yet_',
                    claims: decodedPcpApiToken.claims,
                    expiresAt: decodedPcpApiToken.expiresAt,
                    issuedAt: decodedPcpApiToken.issuedAt,
                }
                this.saveNewTokenData(tokenData);
                this.tokenStorage.setLiveLessonToken(liveLessonTokenData.token);
                this.store.dispatch(liveLessonAuthActions.userLoginSuccess({
                    tokenData: {
                        ...tokenData,
                        liveLesson: decodedToken,
                    }
                }));
                this.patchState({ isAuthenticated: true });

                return tokenData;
            }),
            catchError(err => {
                const error = err instanceof HttpErrorResponse ? err.error : err;
                this.clearToken();
                this.store.dispatch(liveLessonAuthActions.userLoginFailed({ error }));
                this.patchState({ loginFailedReason: error.message, isAuthenticated: false });

                throw error;
            }),
        );
    }

    /**
     * Implementation of TokenRefreshDelegate interface.
     * This is the actual token refresh operation that the orchestrator delegates to.
     */
    performTokenRefresh(token: string, refreshToken: string): Observable<TokensAndDecodedToken> {
        return this.authApiService
            .refreshToken(token, refreshToken)
            .pipe(
                this.handleTokenRefreshResponse()
            );
    }


    private handleTokenRefreshResponse<T extends TokensAndDecodedToken>() {
        return (response: Observable<T>) => response.pipe(
            map((tokenData: TokensAndDecodedToken) => {
                this.saveNewTokenData(tokenData);
                this.store.dispatch(authServiceActions.tokenRefreshSuccess({ tokenData }));
                this.patchState({ isAuthenticated: true });

                return tokenData;
            }),
            catchError(error => {
                this.clearToken();
                this.store.dispatch(authServiceActions.tokenRefreshFailed({ error }));
                this.patchState({ isAuthenticated: false });

                throw error;
            }),
            finalize(() => {
                this.patchState({ isRefreshingToken: false });
            }),
        );
    }



    private saveNewTokenData(tokenData: TokensAndDecodedToken) {
        this.patchState({
            tokenData,
            isInitialized: true,
            isRefreshingToken: false,
            isLoggingIn: false,
        });
        this.tokenStorage.setAccessToken(tokenData.token);
        this.tokenStorage.setRefreshToken(tokenData.refreshToken);
        this.patchState({ isAuthenticated: true });
    }

    private clearToken() {
        this.patchState({
            tokenData: undefined,
            isLoggingIn: false,
            isRefreshingToken: false,
            isInitialized: true,
            isAuthenticated: false,
        });
        this.tokenStorage.clear();
    }

}
